import React from 'react';
import {
  Card,
  CardContent,
  Input,
  Button,
  Badge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@tonic/shared/components/ui';
import { Search, MoreVertical, Plus, Calendar, Utensils, ArrowLeftRight } from 'lucide-react';
import { DataTableWrapper } from '@/features/shared/components/data-display/DataTableWrapper';
import { mockRefillContainers } from './mock-data';

const columns = [
  {
    accessorKey: 'id',
    header: 'Container ID',
    cell: ({ row }) => (
      <div className="font-medium">{row.original.id}</div>
    )
  },
  {
    accessorKey: 'name',
    header: 'Container',
    cell: ({ row }) => (
      <div className="flex items-center">
        {row.original.name.includes('HUVUDROLL') && (
          <Utensils className="w-4 h-4 mr-2 text-[#0058AB]" />
        )}
        <div>
          <div className="font-medium">{row.original.name}</div>
          <div className="text-sm text-muted-foreground">{row.original.sku}</div>
        </div>
      </div>
    )
  },
  {
    accessorKey: 'customer',
    header: 'Customer',
    cell: ({ row }) => row.original.currentCustomer ? (
      <div>
        <div className="font-medium">{row.original.currentCustomer.name}</div>
        <div className="text-sm text-muted-foreground">{row.original.currentCustomer.email}</div>
      </div>
    ) : (
      <span className="text-muted-foreground">-</span>
    )
  },
  {
    accessorKey: 'condition',
    header: 'Return Condition',
    cell: ({ row }) => (
      <Badge variant={getConditionVariant(row.original.condition)}>
        {row.original.condition}
      </Badge>
    )
  },
  {
    accessorKey: 'lastRefill',
    header: 'Last Refill',
    cell: ({ row }) => (
      <div className="flex items-center">
        <Calendar className="w-4 h-4 mr-2 text-muted-foreground" />
        {new Date(row.original.lastRefill).toLocaleDateString()}
      </div>
    )
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <Badge variant={getStatusVariant(row.original.status)}>
        {row.original.status}
      </Badge>
    )
  },
  {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">Open menu</span>
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem>Process Return</DropdownMenuItem>
          <DropdownMenuItem>Schedule Maintenance</DropdownMenuItem>
          <DropdownMenuItem>View History</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }
];

function getConditionVariant(condition) {
  switch (condition?.toLowerCase()) {
    case 'excellent':
      return 'default';
    case 'good':
      return 'secondary';
    case 'fair':
      return 'outline';
    case 'poor':
      return 'destructive';
    default:
      return 'outline';
  }
}

function getStatusVariant(status) {
  switch (status?.toLowerCase()) {
    case 'active':
      return 'default';
    case 'maintenance':
      return 'destructive';
    case 'in_use':
      return 'secondary';
    default:
      return 'outline';
  }
}

export function RefillReturnsPage() {
  const [filters, setFilters] = React.useState({
    search: '',
    condition: 'all',
    status: 'all'
  });

  // Filter to show only containers in maintenance or poor condition
  const returns = React.useMemo(() => {
    let filteredReturns = mockRefillContainers.filter(container =>
      container.status === 'maintenance' || container.condition === 'poor'
    );

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filteredReturns = filteredReturns.filter(container =>
        container.name.toLowerCase().includes(searchLower) ||
        container.sku.toLowerCase().includes(searchLower) ||
        container.currentCustomer?.name.toLowerCase().includes(searchLower)
      );
    }

    if (filters.condition !== 'all') {
      filteredReturns = filteredReturns.filter(container =>
        container.condition.toLowerCase() === filters.condition.toLowerCase()
      );
    }

    if (filters.status !== 'all') {
      filteredReturns = filteredReturns.filter(container =>
        container.status.toLowerCase() === filters.status.toLowerCase()
      );
    }

    return filteredReturns;
  }, [filters]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Returns & Maintenance</h1>
          <p className="text-sm text-muted-foreground">
            Process returned containers and manage maintenance
          </p>
        </div>
        <Button>
          <Plus className="w-4 h-4 mr-2" />
          Process Return
        </Button>
      </div>

      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
            <div className="relative flex-1 w-full md:max-w-sm">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-muted-foreground" />
              </div>
              <Input
                placeholder="Search returns..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-9 w-full"
              />
            </div>

            <div className="flex gap-4 items-center w-full md:w-auto">
              <Select
                value={filters.condition}
                onValueChange={(value) => setFilters(prev => ({ ...prev, condition: value }))}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by condition" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Conditions</SelectItem>
                  <SelectItem value="excellent">Excellent</SelectItem>
                  <SelectItem value="good">Good</SelectItem>
                  <SelectItem value="fair">Fair</SelectItem>
                  <SelectItem value="poor">Poor</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={filters.status}
                onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="ready">Ready</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <DataTableWrapper
            columns={columns}
            data={returns}
            emptyMessage="No returns found"
            loadingMessage="Loading returns..."
          />
        </CardContent>
      </Card>

      {/* Special Meatball Returns Notice */}
      <Card className="bg-[#FFDB00] border-[#0058AB] border-2">
        <CardContent className="py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <ArrowLeftRight className="w-12 h-12 text-[#0058AB]" />
              <div>
                <h2 className="text-xl font-bold text-[#0058AB]">HUVUDROLL Container Returns</h2>
                <p className="text-[#0058AB]">
                  Priority processing for meatball containers to maintain freshness standards!
                </p>
              </div>
            </div>
            <Button className="bg-[#0058AB] hover:bg-[#004F9B]">
              <Plus className="w-4 h-4 mr-2" />
              Process Meatball Return
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 
